import React from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, 
  RouterProvider } from 'react-router-dom';
import CabbAppBar from './components/CabbAppBar';
import CorrelatorConfigurationView from './schedule/CorrelatorConfigurationView';
import Schedule from './schedule/Schedule';
import TargetCatalogueView from './schedule/TargetCatalogueView';
import ScheduleContextProvider from './schedule/ScheduleContext';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, AuthSession, fetchAuthSession} from 'aws-amplify/auth'
import { get } from 'aws-amplify/api';

import "./App.css";
import { Alert, createTheme, Snackbar, ThemeProvider } from '@mui/material';
import Editor from './Editor';
import * as ATCAConstants from './util/ATCAConstants';

const SERVER_ROOT_URL = process.env.REACT_APP_SERVER_ROOT_URL;
const COGNITO_URL = process.env.REACT_APP_COGNITO_URL;

const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;
const WEB_CLIENT_ID = process.env.REACT_APP_WEB_CLIENT_ID;

// const NEXUS_PROVIDER_NAME = 'CSIRONexus';

Amplify.configure({
  "API": {
    "REST": {
      "cabb": {
        "endpoint": SERVER_ROOT_URL||''
      }
    }
  },
  Auth: {
    Cognito: {
      userPoolId: USER_POOL_ID||'',
      userPoolClientId: WEB_CLIENT_ID||'',

      loginWith: {
        oauth: {
          domain: COGNITO_URL||'',
          scopes: [
            'aws.cognito.signin.user.admin',
            'email',
            'openid',
          ],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
        }
      }
    }
  }
}, {
  API: {
    REST: {
      headers: async () => {
        const session = await fetchAuthSession();
        return { Authorization: session.tokens?.idToken?.toString()||'' };
      }
    }
  }
});

const router = createBrowserRouter(
  createRoutesFromElements(
        <Route path='/' element={<Editor />}>
            <Route path='target-catalogue' element={
              <TargetCatalogueView />
            } />
            <Route path='schedule-editor' element={
              <ScheduleContextProvider>
                <Schedule />
              </ScheduleContextProvider>
            } />
            <Route path='correlator-setting' element={
              <CorrelatorConfigurationView />
            } />
        </Route>
  )
);
function App() {
  const [authorised, setAuthorised] = React.useState(false);
  const [email, setEmail] = React.useState<string>('');
  const [snackMessage, setSnackMessage] = React.useState<any>(null);

  // check if a user is loggin, redirect to login page if not
  const getLoginUser = () => {
    fetchAuthSession()
      .then((session: AuthSession) => {
        if (session['tokens']) {
          const payload = (session['tokens']['idToken']||{})['payload']||{};
          console.log(`user: ${payload['email']} groups: ${payload['cognito:groups']}`);
          setAuthorised(true);
          setEmail(payload['email']?.toString()||'');
        } else {
          setAuthorised(true);
          setEmail('');
          console.log('need to login');
          signIn();
        }
      }).catch((err: any) => {
        console.log('not logged in: ' + err);
        signIn();
      });
  }

  const signIn = () => {
    signInWithRedirect().then(() => {
      getLoginUser();
    }).catch((err: any) => {
      console.log('Could not login', err);
      setAuthorised(false);
      setEmail('');
    });
  }

  React.useEffect(() => {
    getLoginUser();

    const getSettings = get(
      {
        apiName: 'cabb',
        path: '/get_all_configs',
      }
    );

    getSettings.response
      .then(response => response.body.json())
      .then((response: any) => {        
        ATCAConstants.set_schedule_schema(response['schema']);
        ATCAConstants.set_band_settings(response['band_settings']);
        ATCAConstants.set_array_configuration(response['array_config']);
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not retrieve settings from server!'
        });
      });

  }, []);
  
  const theme = createTheme({
    palette: {
      primary: {
        main: '#004B87',
        light: '#00A9CE',
        dark: '#142C3F',
        // contrastText: will be calculated to contrast with palette.primary.main
      },
    },
    components: {
      // Name of the component
      MuiButtonBase: {
        defaultProps: {
          // The props to change the default for.
          disableRipple: true, // No more ripple, on the whole application 💣!
        },
      },
    },
  });
  
  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <Snackbar
          role = "alert"
          aria-label = {"Alert message"}
          open={Boolean(snackMessage)}
          autoHideDuration={6000}
          onClose={e => setSnackMessage(null)}
        >
          <Alert severity={snackMessage ? snackMessage.severity : 'error' }>
            {snackMessage ? snackMessage.message : ''}
          </Alert>
        </Snackbar>
        <CabbAppBar email={email} authorised={authorised}
          refreshUser={getLoginUser}/>
        {authorised ? 
          <RouterProvider router={router} />
          :
          (
            <Alert severity="error">
              You are not authorised to access this application. Please contact 'email' for help.
            </Alert>
          )
        }
      </ThemeProvider>
    </div>
  );
}

export default App;
